import React, { useState, useEffect } from 'react';
import PersonalInfo from "./PersonalInfo";
import WorkExperience from "./WorkExperience";
import Education from "./Education";
import Skills from "./Skills";
//import Projects from "./Projects";
import Resume from "./Resume";
import { getProfile } from '../../../services/candidate/profileService';
import { toast } from 'react-toastify';

const Profile = () => {
  const [activeSection, setActiveSection] = useState('personal');
  const [profileData, setProfileData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [profileCompletion, setProfileCompletion] = useState(0);

  // Fetch profile data on component mount
  useEffect(() => {
    fetchProfileData();
  }, []);

  const fetchProfileData = async () => {
    try {
      setLoading(true);
      const response = await getProfile();
      //console.log('Profile API response:', response);

      if (response.success) {
        setProfileData(response.data);
        setProfileCompletion(response.data.completionData.overallScore);
      } else {
        toast.error('Failed to load profile data');
      }
      
    } catch (error) {
      console.error('Error fetching profile:', error);
      toast.error('Error loading profile data');
    } finally {
      setLoading(false);
    }
  };

  const updateProfileCompletion = (data) => {
    if (data?.completionData?.overallScore !== undefined) {
      setProfileCompletion(data.completionData.overallScore);
    }
  };

  // Format sections from API response
  const getSections = () => {
    if (!profileData?.completionData?.sections) {
      return [
        { id: 'personal', label: 'Personal Info', icon: 'fas fa-user', completion: 0 },
        { id: 'work', label: 'Work Experience', icon: 'fas fa-briefcase', completion: 0 },
        { id: 'education', label: 'Education', icon: 'fas fa-graduation-cap', completion: 0 },
        { id: 'skills', label: 'Skills', icon: 'fas fa-code', completion: 0 },
        //{ id: 'projects', label: 'Projects', icon: 'fas fa-project-diagram', completion: 0 },
        { id: 'resume', label: 'Resume', icon: 'fas fa-file-pdf', completion: 0 }
      ];
    }

    const sectionMap = {
      personalInfo: { id: 'personal', icon: 'fas fa-user' },
      workExperience: { id: 'work', icon: 'fas fa-briefcase' },
      education: { id: 'education', icon: 'fas fa-graduation-cap' },
      skills: { id: 'skills', icon: 'fas fa-code' },
      //projects: { id: 'projects', icon: 'fas fa-project-diagram' },
      resume: { id: 'resume', icon: 'fas fa-file-pdf' }
    };

    return profileData.completionData.sections.map(section => ({
      id: sectionMap[section.name]?.id || section.name,
      label: section.displayName,
      icon: sectionMap[section.name]?.icon || 'fas fa-circle',
      completion: section.score || 0,
      isCompleted: section.isCompleted
    }));
  };

  const profileStats = profileData ? {
    completeness: profileData.completionData.overallScore,
    aiScore: 87, // This would come from a different API
    lastUpdated: profileData.profile.updatedAt || 'Not updated yet',
    views: 45, // This would come from a different API
    matches: 12 // This would come from a different API
  } : {
    completeness: 0,
    aiScore: 0,
    lastUpdated: 'Not updated yet',
    views: 0,
    matches: 0
  };

  const renderSection = () => {
    if (loading) {
      return (
        <div className="flex justify-center items-center h-64">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600"></div>
        </div>
      );
    }

    const sectionProps = {
      profileData: profileData,
      onUpdate: fetchProfileData
    };

    switch (activeSection) {
      case 'personal':
        return <PersonalInfo {...sectionProps} />;
      case 'work':
        return <WorkExperience {...sectionProps} />;
      case 'education':
        return <Education {...sectionProps} />;
      case 'skills':
        return <Skills {...sectionProps} />;
      /* case 'projects':
        return <Projects {...sectionProps} />; */
      case 'resume':
        return <Resume {...sectionProps} />;
      default:
        return <PersonalInfo {...sectionProps} />;
    }
  };

  if (loading && !profileData) {
    return (
      <div className="flex justify-center items-center h-screen">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600"></div>
      </div>
    );
  }

  const sections = getSections();
  const activeSectionData = sections.find(s => s.id === activeSection);

  return (
    <div>
      {/* SEO Settings */}
      <title>{`My Profile – Showcase Skills & Experience | ${process.env.REACT_APP_NAME}`}</title>
      <meta name="robots" content="noindex, nofollow" />

      {/* Header */}
      <div className="mb-8">
        <h1 className="text-2xl font-bold text-white">My Profile</h1>
        <p className="text-gray-400">Manage your professional profile and increase your job match score</p>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-4 gap-6">
        {/* Left Sidebar - Navigation */}
        <div className="lg:col-span-1">
          <div className="bg-white rounded-xl shadow-sm p-6 sticky top-6 flex flex-col md:h-[calc(100vh-5rem)]">
            {/* Profile Completion */}
            <div className="mb-6">
              <div className="flex justify-between items-center mb-2">
                <span className="text-sm font-medium text-gray-700">Profile Completion</span>
                <span className="text-sm font-bold text-primary-600">{profileStats.completeness}%</span>
              </div>
              <div className="w-full bg-gray-200 rounded-full h-2">
                <div 
                  className="bg-green-500 h-2 rounded-full transition-all duration-300"
                  style={{ width: `${profileStats.completeness}%` }}
                ></div>
              </div>
              <p className="text-xs text-gray-500 mt-2">
                Complete your profile to increase job matches by {100 - profileStats.completeness}%
              </p>
            </div>

            {/* Navigation */}
            <nav className="space-y-2">
              {sections.map((section) => (
                <button
                  key={section.id}
                  onClick={() => setActiveSection(section.id)}
                  className={`w-full flex items-center justify-between p-3 rounded-lg text-left transition-colors ${
                    activeSection === section.id
                      ? 'bg-primary-50 text-primary-700 border border-primary-200'
                      : 'text-gray-700 hover:bg-gray-50'
                  }`}
                >
                  <div className="flex items-center">
                    <i className={`${section.icon} mr-3 w-5 text-center`}></i>
                    <span className="font-medium">{section.label}</span>
                  </div>
                  <div className="flex items-center space-x-2">
                    <div className={`w-2 h-2 rounded-full ${
                      section.completion === 100 ? 'bg-green-500' :
                      section.completion >= 70 ? 'bg-yellow-500' :
                      'bg-red-500'
                    }`}></div>
                    <span className="text-xs text-gray-500">{section.completion}%</span>
                  </div>
                </button>
              ))}
            </nav>

            {/* Profile Stats */}
            {/* <div className="mt-8 pt-6 border-t border-gray-200">
              <h3 className="text-sm font-semibold text-gray-800 mb-4">Profile Stats</h3>
              <div className="space-y-3">
                <div className="flex justify-between items-center">
                  <span className="text-sm text-gray-600">AI Match Score</span>
                  <span className="text-sm font-medium text-gray-800">{profileStats.aiScore}%</span>
                </div>
                <div className="flex justify-between items-center">
                  <span className="text-sm text-gray-600">Profile Views</span>
                  <span className="text-sm font-medium text-gray-800">{profileStats.views}</span>
                </div>
                <div className="flex justify-between items-center">
                  <span className="text-sm text-gray-600">Job Matches</span>
                  <span className="text-sm font-medium text-gray-800">{profileStats.matches}</span>
                </div>
                <div className="flex justify-between items-center">
                  <span className="text-sm text-gray-600">Last Updated</span>
                  <span className="text-sm text-gray-800">
                    {profileStats.lastUpdated === 'Not updated yet' 
                      ? 'Not updated yet'
                      : new Date(profileStats.lastUpdated).toLocaleDateString()
                    }
                  </span>
                </div>
              </div>
            </div> */}

            {/* Quick Actions */}
            {/* <div className="mt-6 pt-6 border-t border-gray-200">
              <h3 className="text-sm font-semibold text-gray-800 mb-4">Quick Actions</h3>
              <div className="space-y-2">
                <button className="w-full text-left p-2 text-sm text-primary-600 hover:bg-primary-50 rounded-lg flex items-center">
                  <i className="fas fa-download mr-2"></i>
                  Download Profile PDF
                </button>
                <button className="w-full text-left p-2 text-sm text-primary-600 hover:bg-primary-50 rounded-lg flex items-center">
                  <i className="fas fa-share-alt mr-2"></i>
                  Share Profile
                </button>
                <button className="w-full text-left p-2 text-sm text-green-600 hover:bg-green-50 rounded-lg flex items-center">
                  <i className="fas fa-eye mr-2"></i>
                  Preview Profile
                </button>
              </div>
            </div> */}
          </div>
        </div>

        {/* Main Content */}
        <div className="lg:col-span-3">
          <div className="bg-white rounded-xl shadow-sm">
            {/* Section Header */}
            <div className="p-6 border-b border-gray-200">
              <div className="flex justify-between items-center">
                <div>
                  <h2 className="text-xl font-semibold text-gray-800">
                    {activeSectionData?.label || 'Personal Info'}
                  </h2>
                  <p className="text-gray-600 text-sm mt-1">
                    Manage your {(activeSectionData?.label || 'personal info').toLowerCase()} information
                  </p>
                </div>
                <div className="flex items-center space-x-3">
                  <div className={`px-3 py-1 rounded-full text-xs font-medium ${
                    activeSectionData?.completion === 100 ? 'bg-green-100 text-green-800' :
                    activeSectionData?.completion >= 70 ? 'bg-yellow-100 text-yellow-800' :
                    'bg-red-100 text-red-800'
                  }`}>
                    {activeSectionData?.completion || 0}% Complete
                  </div>
                </div>
              </div>
            </div>

            {/* Section Content */}
            <div className="p-6">
              {renderSection()}
            </div>
          </div>

          {/* AI Recommendations */}
          <div className="mt-6 bg-white rounded-xl shadow-sm p-6">
            <h3 className="text-lg font-semibold text-gray-800 mb-4">Profile Recommendations</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="flex items-start">
                <i className="fas fa-lightbulb text-yellow-500 mt-1 mr-3"></i>
                <div>
                  <p className="font-medium text-gray-800">Add 3 more skills</p>
                  <p className="text-sm text-gray-600">Adding a few relevant skills can significantly improve your profile matches.</p>
                </div>
              </div>
              <div className="flex items-start">
                <i className="fas fa-chart-line text-green-500 mt-1 mr-3"></i>
                <div>
                  <p className="font-medium text-gray-800">Complete work experience</p>
                  <p className="text-sm text-gray-600">Enhance your experience section by highlighting your roles and key contributions.</p>
                </div>
              </div>
              <div className="flex items-start">
                <i className="fas fa-graduation-cap text-purple-500 mt-1 mr-3"></i>
                <div>
                  <p className="font-medium text-gray-800">Add education</p>
                  <p className="text-sm text-gray-600">Including your educational background helps increase profile visibility and trust.</p>
                </div>
              </div>
              <div className="flex items-start">
                <i className="fas fa-bullseye text-blue-500 mt-1 mr-3"></i>
                <div>
                  <p className="font-medium text-gray-800">Upload updated resume</p>
                  <p className="text-sm text-gray-600">Keeping your resume up to date helps employers see your latest information.</p>
                </div>
              </div>              
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Profile;